import { useState, useEffect, useMemo, useCallback } from 'react';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { Search, Home, Folder, Users, Activity, LogOut, Menu, X, Workflow, Settings, ChevronDown, ChevronRight, SearchCheck, Globe, Bot, Briefcase, HeartPulse, FileSignature, Calculator, Scale, LayoutDashboard, CalendarDays, BarChart3, Cpu, Phone, PhoneCall, Youtube, Presentation, Shield } from 'lucide-react';
import CommandBar from '../components/CommandBar';
import DashboardHome from '../components/DashboardHome';
import ProjectsView from '../components/ProjectsView';
import UsersView from '../components/UsersView';
import ActivityFeed from '../components/ActivityFeed';
import WorkflowsHub from './WorkflowsHub';
import KeywordResearch from './KeywordResearch';
import MeetingsPulse from './MeetingsPulse';
import SOWBuilder from './SOWBuilder';
import EstimatorsView from './EstimatorsView';
import LegalDocsView from './LegalDocsView';
import ProjectDetail from './ProjectDetail';
import CreateBrief from './CreateBrief';
import BriefDetail from './BriefDetail';
import SettingsView from '../components/SettingsView';
import MondayBoardsView from './MondayBoardsView';
import CalendarView from './CalendarView';
import PaidSearchAudit from './PaidSearchAudit';
import OptimizationCenter from './OptimizationCenter';
import Calls from './Calls';
import SoftphoneEmbed from './SoftphoneEmbed';
import YouTubeTeamWorkspace from './YouTubeTeamWorkspace';
import DecksView from './DecksView';
import ComplianceCenter from './ComplianceCenter';
import Softphone from '../components/Softphone';
import QuickDmModal from '../components/QuickDmModal';

const InternalDashboard = ({ user, onLogout }) => {
  const [showCommandBar, setShowCommandBar] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [expandedSections, setExpandedSections] = useState({ workflows: true, clientMgmt: true });
  const navigate = useNavigate();
  const location = useLocation();

  const userRoles = useMemo(() => user.roles || [user.role], [user.roles, user.role]);
  const hasRole = useCallback((...roles) => roles.some(r => userRoles.includes(r)), [userRoles]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowCommandBar(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    if (location.pathname.includes('/workflows') || location.pathname.includes('/keywords')) {
      setExpandedSections(prev => ({ ...prev, workflows: true }));
    }
    if (location.pathname.includes('/client-management')) {
      setExpandedSections(prev => ({ ...prev, clientMgmt: true }));
    }
  }, [location.pathname]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const isActive = (path) => location.pathname === path || (path !== '/internal' && location.pathname.startsWith(path));

  const topMenuItems = [
    { icon: Home, label: 'Dashboard', path: '/internal', roles: ['Admin', 'Media_Buyer', 'Creative', 'Analyst', 'Client', 'Client_Service'] },
    { icon: Folder, label: 'Projects', path: '/internal/projects', roles: ['Admin', 'Media_Buyer', 'Client_Service'] },
  ];

  const searchMarketingItems = [
    { icon: SearchCheck, label: 'Paid Search', path: '/internal/workflows', roles: ['Admin', 'Media_Buyer'] },
    { icon: BarChart3, label: 'Search Audits', path: '/internal/workflows/audits', roles: ['Admin', 'Media_Buyer'] },
    { icon: Cpu, label: 'Optimization Center', path: '/internal/workflows/optimization', roles: ['Admin', 'Media_Buyer'] },
    { icon: Youtube, label: 'YouTube Team', path: '/internal/optimization/youtube', roles: ['Admin', 'Media_Buyer'] },
    { icon: Search, label: 'Keyword Research', path: '/internal/keywords', roles: ['Admin', 'Media_Buyer'] },
    { icon: Globe, label: 'SEO', path: '/internal/workflows/seo', coming: true },
    { icon: Bot, label: 'AIO', path: '/internal/workflows/aio', coming: true },
  ];

  const clientMgmtItems = [
    { icon: HeartPulse, label: 'Meetings & Pulse', path: '/internal/client-management/meetings', roles: ['Admin', 'Client_Service'] },
    { icon: FileSignature, label: 'SOW Builder', path: '/internal/client-management/sow', roles: ['Admin', 'Client_Service'] },
    { icon: Calculator, label: 'Estimators', path: '/internal/client-management/estimators', roles: ['Admin', 'Client_Service'] },
    { icon: Scale, label: 'Legal Docs', path: '/internal/client-management/legal', roles: ['Admin', 'Client_Service'] },
    { icon: Presentation, label: 'Skill Studio', path: '/internal/client-management/decks' },
    { icon: LayoutDashboard, label: 'Monday Boards', path: '/internal/client-management/monday' },
    { icon: CalendarDays, label: 'Calendar', path: '/internal/client-management/calendar' },
    { icon: Phone, label: 'Calls & Voicemail', path: '/internal/client-management/calls' },
    { icon: PhoneCall, label: 'Browser Softphone', path: '/internal/client-management/softphone' },
  ];

  const bottomMenuItems = [
    { icon: Users, label: 'Users', path: '/internal/users', roles: ['Admin'] },
    { icon: Shield, label: 'Compliance', path: '/internal/compliance', roles: ['Admin'] },
    { icon: Activity, label: 'Activity', path: '/internal/activity', roles: ['Admin', 'Media_Buyer'] },
    { icon: Settings, label: 'Settings', path: '/internal/settings', roles: ['Admin'] },
  ];

  const NavLink = ({ item }) => (
    <Link
      to={item.coming ? '#' : item.path}
      onClick={item.coming ? (e) => e.preventDefault() : undefined}
      className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-all duration-200 ease-productive ${
        isActive(item.path) && !item.coming
          ? 'bg-brand/8 text-brand font-semibold'
          : item.coming
          ? 'text-text-muted cursor-default'
          : 'text-text-secondary hover:bg-surface-page hover:text-text-primary'
      }`}
      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <item.icon size={18} />
      <span>{item.label}</span>
      {item.coming && (
        <span className="ml-auto text-[10px] font-semibold uppercase tracking-wider text-text-muted bg-surface-page px-1.5 py-0.5 rounded">Soon</span>
      )}
    </Link>
  );

  const filteredTopMenu = useMemo(() => topMenuItems.filter(i => i.roles.some(r => hasRole(r))), [hasRole]); // eslint-disable-line react-hooks/exhaustive-deps
  const filteredSearchMarketing = useMemo(() => searchMarketingItems.filter(i => !i.roles || i.roles.some(r => hasRole(r))), [hasRole]); // eslint-disable-line react-hooks/exhaustive-deps
  const filteredBottomMenu = useMemo(() => bottomMenuItems.filter(i => i.roles.some(r => hasRole(r))), [hasRole]); // eslint-disable-line react-hooks/exhaustive-deps

  const isWorkflowActive = location.pathname.includes('/workflows') || location.pathname.includes('/keywords');
  const isClientMgmtActive = location.pathname.includes('/client-management');

  return (
    <div className="min-h-screen bg-surface-page flex" data-testid="internal-dashboard">
      {/* Sidebar */}
      <aside
        className={`${
          sidebarOpen ? 'w-[260px]' : 'w-0'
        } transition-all duration-300 bg-white border-r border-border-subtle flex-shrink-0 overflow-hidden flex flex-col`}
        data-testid="sidebar"
      >
        <div className="px-5 pt-6 pb-4 flex items-center gap-3">
          <img src="/bcm-logo.png" alt="BCM" className="w-8 h-8 object-contain" />
          <div>
            <h1 className="text-xl font-bold tracking-tight text-text-primary">Ventas OS</h1>
            <p className="text-xs text-text-tertiary mt-0.5">Internal workspace</p>
          </div>
        </div>

        <nav className="flex-1 px-3 space-y-0.5 overflow-y-auto">
          {filteredTopMenu.map((item) => (
            <NavLink key={item.path} item={item} />
          ))}

          {/* Workflows section */}
          {hasRole('Admin', 'Media_Buyer', 'Media_Analyst') && (
            <div className="pt-3">
              <button
                onClick={() => toggleSection('workflows')}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-all duration-200 ease-productive ${
                  isWorkflowActive ? 'text-brand' : 'text-text-secondary hover:bg-surface-page hover:text-text-primary'
                }`}
                data-testid="nav-workflows-toggle"
              >
                <Workflow size={18} />
                <span className="font-medium">Workflows</span>
                <span className="ml-auto">
                  {expandedSections.workflows ? <ChevronDown size={14} className="text-text-muted" /> : <ChevronRight size={14} className="text-text-muted" />}
                </span>
              </button>

              {expandedSections.workflows && (
                <div className="ml-4 pl-3 border-l border-border-subtle mt-1 space-y-0.5">
                  <p className="px-3 pt-1 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.1em] text-text-muted">
                    Search Marketing
                  </p>
                  {filteredSearchMarketing.map((item) => (
                    <NavLink key={item.path} item={item} />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Client Management */}
          {hasRole('Admin', 'Client_Service') && (
            <div className="pt-1">
              <button
                onClick={() => toggleSection('clientMgmt')}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-all duration-200 ease-productive ${
                  isClientMgmtActive ? 'text-brand' : 'text-text-secondary hover:bg-surface-page hover:text-text-primary'
                }`}
                data-testid="nav-client-management-toggle"
              >
                <Briefcase size={18} />
                <span className="font-medium">Client Management</span>
                <span className="ml-auto">
                  {expandedSections.clientMgmt ? <ChevronDown size={14} className="text-text-muted" /> : <ChevronRight size={14} className="text-text-muted" />}
                </span>
              </button>

              {expandedSections.clientMgmt && (
                <div className="ml-4 pl-3 border-l border-border-subtle mt-1 space-y-0.5">
                  {clientMgmtItems.map((item) => (
                    <NavLink key={item.path} item={item} />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Bottom items */}
          <div className="pt-3">
            {filteredBottomMenu.map((item) => (
              <NavLink key={item.path} item={item} />
            ))}
          </div>
        </nav>

        {/* User card + search trigger */}
        <div className="p-3 border-t border-border-subtle space-y-3">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="w-9 h-9 rounded-full bg-brand/10 flex items-center justify-center text-brand font-semibold text-sm">
              {user.name.charAt(0)}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-text-primary truncate">{user.name}</p>
              <p className="text-xs text-text-tertiary">{user.role}</p>
            </div>
            <button
              onClick={onLogout}
              className="p-1.5 rounded-md text-text-muted hover:text-error hover:bg-error/10 transition-colors"
              data-testid="logout-button"
              title="Logout"
            >
              <LogOut size={16} />
            </button>
          </div>

          <button
            onClick={() => setShowCommandBar(true)}
            className="w-full flex items-center justify-between px-3 py-2.5 rounded-md border border-border-subtle bg-surface-page text-text-muted hover:bg-white transition-colors text-sm"
            data-testid="cmd-bar-trigger"
          >
            <div className="flex items-center gap-2">
              <Search size={15} />
              <span>Search...</span>
            </div>
            <kbd className="px-1.5 py-0.5 text-[11px] font-medium text-text-tertiary bg-white border border-border-subtle rounded">&#8984;K</kbd>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        {/* Mobile Header */}
        <div className="md:hidden flex items-center justify-between px-4 py-3 border-b border-border-subtle bg-white">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-md hover:bg-surface-page"
            data-testid="mobile-menu-toggle"
          >
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
          <h1 className="text-base font-semibold text-text-primary">Ventas OS</h1>
          <div className="w-8" />
        </div>

        <div className="p-6 lg:p-8 mx-auto max-w-[1600px]">
          <Routes>
            <Route path="/" element={<DashboardHome user={user} />} />
            <Route path="/projects" element={<ProjectsView user={user} />} />
            <Route path="/projects/:projectId" element={<ProjectDetail user={user} />} />
            <Route path="/workflows" element={<WorkflowsHub user={user} />} />
            <Route path="/workflows/briefs/create" element={<CreateBrief user={user} />} />
            <Route path="/workflows/briefs/:briefId" element={<BriefDetail user={user} />} />
            <Route path="/workflows/audits" element={<PaidSearchAudit user={user} />} />
            <Route path="/workflows/optimization" element={<OptimizationCenter user={user} />} />
            <Route path="/keywords" element={<KeywordResearch user={user} />} />
            <Route path="/client-management/meetings" element={<MeetingsPulse user={user} />} />
            <Route path="/client-management/sow" element={<SOWBuilder user={user} />} />
            <Route path="/client-management/estimators" element={<EstimatorsView user={user} />} />
            <Route path="/client-management/legal" element={<LegalDocsView user={user} />} />
            <Route path="/client-management/decks" element={<DecksView user={user} />} />
            <Route path="/compliance" element={<ComplianceCenter user={user} />} />
            <Route path="/client-management/monday" element={<MondayBoardsView user={user} />} />
            <Route path="/client-management/calendar" element={<CalendarView user={user} />} />
            <Route path="/client-management/calls" element={<Calls user={user} />} />
            <Route path="/client-management/softphone" element={<SoftphoneEmbed user={user} />} />
            <Route path="/optimization/youtube" element={<YouTubeTeamWorkspace user={user} />} />
            <Route path="/users" element={<UsersView user={user} />} />
            <Route path="/activity" element={<ActivityFeed user={user} />} />
            <Route path="/settings" element={<SettingsView user={user} />} />
          </Routes>
        </div>
      </main>

      {showCommandBar && <CommandBar user={user} onClose={() => setShowCommandBar(false)} />}
      {/* Floating JsSIP softphone is dormant pending Telzio support clarifying
          the WSS path & SIP token model. The user is using the embedded Telzio
          dashboard at /internal/client-management/softphone instead. */}
      {/* <Softphone /> */}
      <QuickDmModal />
    </div>
  );
};

export default InternalDashboard;
